/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import { useDispatch, useSelector } from 'react-redux';
import {
  Loader,
  Center,
  Text,
  Menu,
  ActionIcon,
  Flex,
  Button,
} from '@mantine/core';
import routes from '@/routes';
import { Page } from '@/components';
import { useTranslation } from 'react-i18next';
import {
  useGetAllOffersMutation,
  useGetSummeryfileMutation,
  useSendingToTheCustomerForSignatureMutation,
  useGetOfferByIdMutation,
  useUpdateOfferStatusToPaidAndGeneratedPdfMutation,
} from '@/services/api';
import Papa from 'papaparse';
import { getMutationResult } from '@/helpers/query';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import DataTableComponent from '@/components/DataTable';
import { updateBorrower, updateExtraDetails, updateProposal } from '@/data/features/proposal/proposal';
import { STATUS } from '@/helpers/enums';
import { IconDots } from '@tabler/icons-react';
import { formatDateToDDMMYYYY } from '@/helpers/date';
import { jsPDF as JsPDF } from 'jspdf';
// eslint-disable-next-line import/no-unresolved
import { arrayBufferToBase64, rtlFix } from '@/helpers/string';
import autoTable from 'jspdf-autotable';
import assistantFontUrl from '@/assets/fonts/Assistant-Medium.ttf?url';

const Proposals = () => {
  const [data, setData] = useState([]);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fileLoading, setFileLoading] = useState(false);
  const pageSize = 10;
  const { t } = useTranslation();
  const [apiGetAllOffers] = useGetAllOffersMutation();
  const [apiGetSummerylink] = useGetSummeryfileMutation();
  const borrowersStore = useSelector((state) => state.proposal.borrower);
  const userstore = useSelector((state) => state.user);
  const { role } = userstore;
  const [searchParams] = useSearchParams();

  const [apiConfigSendToClientForSignature] = useSendingToTheCustomerForSignatureMutation();
  const formatedData = (date) => dayjs(date).format('DD/MM/YYYY HH:mm');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [apiGetOfferById] = useGetOfferByIdMutation();
  const [filteredRecords, setFilteredRecords] = useState([]);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const { data: result } = await getMutationResult({}, apiGetAllOffers);
        const { success, data: resultData } = result;
        const offersData = resultData.offer.offer;

        if (success) {
          setData(offersData);
        } else {
          setIsError(true);
        }
      } catch (error) {
        setIsError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadFile = async (id) => {
    try {
      setFileLoading(true);
      const { data: d } = await getMutationResult({ id }, apiGetSummerylink);

      const byteArray = new Uint8Array(Object.values(d.data.data));

      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'proposal.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setFileLoading(false);
    }
  };

  const sendToClient = async (record) => {
    try {
      setFileLoading(true);
      const client = borrowersStore
        .find((borrower) => borrower.email === record.firstBorrower.email);

      const objectToSend = {
        proposalNumber: record._id,
        email: client?.email,
        phone: client?.phone,
        fullName: client?.fullName,
      };

      await getMutationResult(
        objectToSend,
        apiConfigSendToClientForSignature,
      );
    } catch {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setFileLoading(false);
    }
  };

  const getSummaryMenuItem = (record) => (
    <Menu.Item
      onClick={() => {
        const isRefinancing = record?.loan?.title === 'mortgageRefinancing';
        // eslint-disable-next-line no-unused-expressions
        isRefinancing ? navigate(
          routeWithParams(routes.mortgageCycleSummary.path, { id: record._id }),
        )
          : navigate(
            routeWithParams(routes.summary.path, { id: record._id }),
          );
      }} >
      {t('pages.proposals.actions.summary')}
    </Menu.Item>
  );

  const handleEdit = async (id) => {
    try {
      const { data: result } = await apiGetOfferById({ id });
      const offer = result?.data ?? null;

      const { borrower } = offer || [];

      const formattedBorrowers = borrower.map((b) => ({
        ...b,
      }));

      dispatch(updateBorrower(formattedBorrowers));
      const objToStore = {
        ...offer,
        loan: offer?.loan?._id,
        youngestBorrowerDOB: formatDateToDDMMYYYY(offer?.youngestBorrowerDOB),
        oldestBorrowerDOB: formatDateToDDMMYYYY(offer?.oldestBorrowerDOB),
        loanTypeName: offer?.loan?.title,
        loanTypeNameHebrew: offer?.loan?.hebrew,
        isRecycling: offer?.isRecycling ? 'yes' : 'no',
        bankAccountRestriction: offer?.bankAccountRestriction ? 'yes' : 'no',
        enforcementFile: offer?.enforcementFile ? 'yes' : 'no',
        returnedChecks: offer?.returnedChecks ? 'yes' : 'no',
        standingOrderReturn: offer?.standingOrderReturn ? 'yes' : 'no',
      };

      dispatch(updateExtraDetails(objToStore));
      dispatch(updateProposal({ id: offer?._id }));
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'borrowerWithId').path, { id }));
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    }
  };
  const [apiUpdateOfferStatus] = useUpdateOfferStatusToPaidAndGeneratedPdfMutation();

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const { data: result } = await getMutationResult({}, apiGetAllOffers);
      const { success, data: resultData } = result;
      const offersData = resultData.offer.offer;

      if (success) {
        setData(offersData);
      } else {
        setIsError(true);
      }
    } catch (error) {
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEditStatus = async (record) => {
    try {
      const result = await apiUpdateOfferStatus({ id: record._id });
      const { data: { success } } = result;
      if (success) {
        fetchOffers();
      }
    } catch (error) {
      console.log({ error });
    }
  };

  const getMenu = (record) => {
    const { name } = record.status;

    switch (name) {
      case 'open':
        return (
          <Menu.Item
          size="xs"
          variant="outline"
          onClick={() => handleEdit(record._id)}
        >
            {t('pages.proposals.actions.edit')}
          </Menu.Item>
        );

      case 'closed':
        return fileLoading ? (
          <Loader />
        ) : (
          <Menu.Item
          size="xs"
          variant="outline"
          onClick={() => downloadFile(record._id)}
        >
            {t('pages.proposals.actions.download')}
          </Menu.Item>
        );

      case 'sentToClient':
        return fileLoading ? (
          <Loader />
        ) : (
          <>
            <Menu.Item
            onClick={() => {
              dispatch(updateBorrower([record.firstBorrower]));
              sendToClient(record);
            }}
          >
              {t('pages.proposals.actions.sentToclient')}
            </Menu.Item>

            {getSummaryMenuItem(record)}
          </>
        );

      case 'pdfGenerated':
        return (
          <>
            {role.code === '3' && (
            <Menu.Item onClick={() => handleEditStatus(record)}>
              {t('pages.proposals.actions.updateStatus')}
            </Menu.Item>
            )}

            {fileLoading ? (
              <Loader />
            ) : (
              <Menu.Item
              size="xs"
              variant="outline"
              onClick={() => downloadFile(record._id)}
            >
                {t('pages.proposals.actions.download')}
              </Menu.Item>
            )}
          </>
        );

      case 'paid':
        return <>{getSummaryMenuItem(record)}</>;

      case 'paidAndGeneratedPdf':
        return fileLoading ? (
          <Loader />
        ) : (
          <Menu.Item
          size="xs"
          variant="outline"
          onClick={() => downloadFile(record._id)}
        >
            {t('pages.proposals.actions.download')}
          </Menu.Item>
        );

      default:
        return null;
    }
  };

  // const getMenu = (record) => (
  //   record.status.name === 'open' ? (
  //     <Menu.Item size="xs" variant="outline"
  //       onClick={() => (handleEdit(record._id))}>
  //       {t('pages.proposals.actions.edit')}
  //     </Menu.Item>
  //   ) : record.status.name === 'closed' ? (
  //     fileLoading ? (
  //       <Loader />
  //     ) : (
  //       <Menu.Item size="xs" variant="outline"
  //         onClick={() => downloadFile(record._id)}>
  //         {t('pages.proposals.actions.download')}
  //       </Menu.Item>
  //     )
  //   ) : record.status.name === 'sentToClient' ? (
  //     fileLoading ? (
  //       <Loader />
  //     ) : (
  //       <>
  //         <Menu.Item
  //           onClick={() => {
  //             dispatch(updateBorrower([record.firstBorrower]));
  //             sendToClient(record);
  //           }}
  //         >
  //           {t('pages.proposals.actions.sentToclient')}
  //         </Menu.Item>
  //         {getSummaryMenuItem(record)}
  //       </>

  //     )
  //   ) : record.status.name === 'pdfGenerated' && role.code === '3' ? (
  //     <Menu.Item
  //       onClick={() => {
  //         handleEditStatus(record);
  //       }}
  //     >
  //       {t('pages.proposals.actions.updateStatus')}
  //           </Menu.Item>

  //         ) : record.status.name === 'paid' ? (
  //         <>
  //               {getSummaryMenuItem(record)}
  //             </>
  //   ) :
  // );

  const [columnFilters, setColumnFilters] = useState(
    {
      agentName: searchParams.get('agentName') || '',
      borrowers: '',
      createdAt: [null, null],
      updatedAt: [null, null],
      status: '',
      firstBorrowerName: searchParams.get('firstBorrowerName') || '',
    },
  );

  const columns = [
    {
      accessor: 'agentName',
      title: t('pages.proposals.headers.agentName'),
      sortable: true,
      isFilterActive: true,
    },
    {
      accessor: 'loanTypeHebrew',
      title: t('pages.proposals.headers.loanTypeHebrew'),
      sortable: true,
      isFilterActive: true,
    },
    {
      accessor: 'firstBorrowerName',
      title: t('pages.proposals.headers.firstBorrowerName'),
      sortable: true,
      isFilterActive: true,
    },
    {
      accessor: 'borrowers',
      title: t('pages.proposals.headers.borrowers'),
      sortable: true,
      isFilterActive: true,
    },
    {
      accessor: 'createdAt',
      title: t('pages.proposals.headers.createdAt'),
      sortable: true,
      isFilterActive: true,
      render: (record) => formatedData(record.createdAt),
      filterType: 'datePicker',
    },
    {
      accessor: 'updatedAt',
      title: t('pages.proposals.headers.updatedAt'),
      sortable: true,
      isFilterActive: true,
      filterType: 'datePicker',
      render: (record) => formatedData(record.updatedAt),
    },
    {
      accessor: 'price',
      title: t('pages.proposals.headers.price'),
      sortable: true,
      isFilterActive: true,
    },
    {
      accessor: 'statusName',
      title: t('pages.proposals.headers.statusName'),
      sortable: true,
      isFilterActive: true,
      filterType: 'select',
      render: (record) => STATUS[record.statusName],
      filterOptions: Object.entries(STATUS).map(([value, label]) => ({
        value,
        label,
      })),
    },
    {
      accessor: 'hasSummeryFile',
      title: t('pages.proposals.headers.actions'),
      render: (record) => <Menu withinPortal position="bottom-end" shadow="sm">
        <Menu.Target>
          <ActionIcon variant="subtle">
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {getMenu(record)}
        </Menu.Dropdown>
      </Menu>,
    },
  ];

  if (loading) {
    return <Center><Loader /></Center>;
  }

  if (isError) {
    return <Center><Text c="red">{t('pages.proposals.noData')}</Text></Center>;
  }

  if (!data || data.length === 0) {
    return <Center><Text>{t('pages.proposals.noData')}</Text></Center>;
  }

  const exportToCsv = () => {
    const source = (filteredRecords?.length ? filteredRecords : data);

    const rows = source.map((r) => ({
      סוכן: r.agentName ?? '',
      'סוג הלוואה': r.loanTypeHebrew ?? '',
      'לווה ראשון': r.firstBorrowerName ?? '',
      לווים: r.borrowers ?? '',
      'נוצר בתאריך': formatedData(r.createdAt),
      'עודכן בתאריך': formatedData(r.updatedAt),
      מחיר: r.price ?? '',
      סטטוס: STATUS[r.statusName] ?? r.statusName ?? '',
    }));

    const csv = Papa.unparse(rows, { quotes: true });
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'proposals.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const loadHebrewFont = async (doc) => {
    const res = await fetch(assistantFontUrl);
    const buf = await res.arrayBuffer();
    const base64 = arrayBufferToBase64(buf);

    doc.addFileToVFS('Assistant-Regular.ttf', base64);
    doc.addFont('Assistant-Regular.ttf', 'Assistant', 'normal');
    doc.setFont('Assistant');
  };

  const exportTableToPdf = async () => {
    try {
      setFileLoading(true);

      const source = (filteredRecords?.length ? filteredRecords : data);
      const doc = new JsPDF('l', 'mm', 'a4');
      await loadHebrewFont(doc);

      const head = [[
        rtlFix(t('pages.proposals.headers.agentName')),
        rtlFix(t('pages.proposals.headers.loanTypeHebrew')),
        rtlFix(t('pages.proposals.headers.firstBorrowerName')),
        rtlFix(t('pages.proposals.headers.borrowers')),
        rtlFix(t('pages.proposals.headers.createdAt')),
        rtlFix(t('pages.proposals.headers.updatedAt')),
        rtlFix(t('pages.proposals.headers.price')),
        rtlFix(t('pages.proposals.headers.statusName')),
      ].reverse()];

      const body = source.map((r) => ([
        rtlFix(r.agentName ?? ''),
        rtlFix(r.loanTypeHebrew ?? ''),
        rtlFix(r.firstBorrowerName ?? ''),
        rtlFix(String(r.borrowers ?? '')),
        rtlFix(formatedData(r.createdAt)),
        rtlFix(formatedData(r.updatedAt)),
        rtlFix(String(r.price ?? '')),
        rtlFix(STATUS[r.statusName] ?? r.statusName ?? ''),
      ].reverse()));

      autoTable(doc, {
        head,
        body,
        theme: 'grid',
        margin: { top: 10, left: 10, right: 10 },
        styles: {
          font: 'Assistant',
          fontStyle: 'normal',
          fontSize: 9,
          cellPadding: 3,
          halign: 'right',
          valign: 'middle',
          lineColor: [220, 224, 229],
          lineWidth: 0.2,
        },
        headStyles: {
          font: 'Assistant',
          fontStyle: 'normal',
          fontSize: 11,
          fillColor: [33, 136, 208],
          halign: 'right',
          valign: 'middle',
          lineColor: [33, 136, 208],
          lineWidth: 0.2,
        },
        pageBreak: 'auto',
      });

      doc.save('proposals.pdf');
    } catch (e) {
      console.error(e);
      notifications.show({
        title: t('globals.error.title'),
        message: e?.message || t('globals.error.message'),
      });
    } finally {
      setFileLoading(false);
    }
  };

  return (
    <Page hideHelp hideNews title={t('pages.proposals.title')}>
      <div>
        <DataTableComponent
          columns={columns}
          data={data}
          loading={loading}
          recordsPerPage={pageSize}
          filters={columnFilters}
          setFilters={setColumnFilters}
          horizontalSpacing="sm"
          onVisibleRecordsChange={({ filteredRecords: fr }) => setFilteredRecords(fr)}
        />

        <Flex justify='flex-start' gap={5}>
          <Button onClick={exportToCsv}>
            {t('pages.proposals.actions.downloadCsv')}
          </Button>
          <Button loading={fileLoading} onClick={exportTableToPdf}>
            {t('pages.proposals.actions.downloadPdf')}
          </Button>
        </Flex>
      </div>
    </Page>
  );
};

export default Proposals;
